import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";
import DeleteIcon from "@mui/icons-material/Delete";
import { addPathInventoryFromOCR } from "../../../services/pathOCRService";
import {
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
} from "../../../utils/formModalStyles";

const PathOCRResultDialog = ({ open, onClose, ocrData, onSuccess }) => {
  const [items, setItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (ocrData && ocrData.items) {
      setItems(ocrData.items.map(item => ({
        ...item,
        originalName: item.name,
        originalPrice: item.price,
        originalQuantity: item.quantity,
        originalCriticalLevel: item.criticalLevel || 10,
      })));
    }
  }, [ocrData]);

  const handleEditItem = (index) => {
    setEditingItem(index);
  };

  const handleSaveItem = (index) => {
    setEditingItem(null);
  };

  const handleCancelEdit = (index) => {
    const item = items[index];
    setItems(prev => prev.map((itm, idx) => 
      idx === index 
        ? {
            ...itm,
            name: itm.originalName,
            price: itm.originalPrice,
            quantity: itm.originalQuantity,
            criticalLevel: itm.originalCriticalLevel,
          }
        : itm
    ));
    setEditingItem(null);
  };

  const handleItemChange = (index, field, value) => {
    setItems(prev => prev.map((item, idx) => 
      idx === index ? { ...item, [field]: value } : item
    ));
  };

  const handleDeleteItem = (index) => {
    setItems(prev => prev.filter((_, idx) => idx !== index));
    // If we're editing the item being deleted, stop editing
    if (editingItem === index) {
      setEditingItem(null);
    } else if (editingItem > index) {
      // Adjust editing index if we deleted an item before the currently editing one
      setEditingItem(editingItem - 1);
    }
  };

  const handleConfirm = async () => {
    if (items.length === 0) {
      setError("No items to add");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const result = await addPathInventoryFromOCR({
        invoiceNumber: ocrData.invoiceNumber,
        invoiceDate: ocrData.invoiceDate,
        supplier: ocrData.supplier,
        totalAmount: ocrData.totalAmount,
        items: items.map(item => ({
          name: item.name,
          unitType: item.unitType || "Each",
          qtyUnit: item.qtyUnit || "piece",
          details: item.details ?? 1,
          requiredQuantity: parseFloat(item.requiredQuantity) || 1,
          cost: parseFloat(item.cost) || 0,
          inventoryQuantity: parseInt(item.inventoryQuantity) || 0,
          batchNumber: item.batchNumber || null,
          expiryDate: item.expiryDate || null,
          amount: parseFloat(item.amount) || 0,
          sgst: parseFloat(item.sgst) || 0,
          cgst: parseFloat(item.cgst) || 0,
          discount: parseFloat(item.discount) || 0,
        }))
      });

      setSuccess(result.message || `Successfully added ${result.successful?.length || 0} items to inventory.`);
      
      setTimeout(() => {
        onSuccess(result);
        handleClose();
      }, 2000);
    } catch (error) {
      console.error("Error adding inventory items:", error);
      setError(error.response?.data?.message || "Failed to add items to inventory. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setItems([]);
    setEditingItem(null);
    setError("");
    setSuccess("");
    setLoading(false);
    onClose();
  };

  if (!ocrData) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper" sx={STANDARD_FORM_DIALOG_PAPER_SX}>
      <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>
        Review Pathology Inventory Items
      </DialogTitle>

      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please review and edit the extracted path inventory items before adding them to your inventory.
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> If any item doesn't exist in the BOM table, it will be automatically created first, then added to inventory.
            </Typography>
          </Alert>

          {items.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>No items remaining:</strong> All extracted items have been deleted. Please re-upload the invoice or add items manually.
              </Typography>
            </Alert>
          )}

          {error && (
            <Alert 
              severity={error.startsWith("Success!") ? "success" : "error"} 
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <TableContainer component={Paper} sx={{ maxHeight: 360, width: "100%", overflow: "auto" }}>
            <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ color: '#000', fontWeight: 'bold', minWidth: 200 }}>Item Name</TableCell>
                  <TableCell sx={{ color: '#000', fontWeight: 'bold', minWidth: 100 }}>Unit Type</TableCell>
                  <TableCell sx={{ color: '#000', fontWeight: 'bold', minWidth: 100 }}>Required Qty</TableCell>
                  <TableCell sx={{ color: '#000', fontWeight: 'bold', minWidth: 80 }}>Cost</TableCell>
                  <TableCell sx={{ color: '#000', fontWeight: 'bold', minWidth: 80 }}>Amount</TableCell>
                  <TableCell sx={{ color: '#000', fontWeight: 'bold', minWidth: 100 }}>Batch No</TableCell>
                  <TableCell sx={{ color: '#000', fontWeight: 'bold', minWidth: 120 }}>Expiry Date</TableCell>
                  <TableCell sx={{ color: '#000', fontWeight: 'bold', minWidth: 80 }}>SGST</TableCell>
                  <TableCell sx={{ color: '#000', fontWeight: 'bold', minWidth: 80 }}>CGST</TableCell>
                  <TableCell sx={{ color: '#000', fontWeight: 'bold', minWidth: 100 }}>Inventory Qty</TableCell>
                  {/* <TableCell sx={{ color: '#000', fontWeight: 'bold', minWidth: 120 }}>Status</TableCell> */}
                  <TableCell sx={{ color: '#000', fontWeight: 'bold', minWidth: 100 }}>Actions</TableCell>
                  <TableCell sx={{ color: '#000', fontWeight: 'bold', minWidth: 80 }}>Delete</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      {editingItem === index ? (
                        <TextField
                          value={item.name}
                          onChange={(e) => handleItemChange(index, "name", e.target.value)}
                          size="small"
                          fullWidth
                          required
                        />
                      ) : (
                        <Typography variant="body2">{item.name}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingItem === index ? (
                        <TextField
                          value={item.unitType}
                          onChange={(e) => handleItemChange(index, "unitType", e.target.value)}
                          size="small"
                          fullWidth
                        />
                      ) : (
                        <Typography variant="body2">{item.unitType || "pieces"}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingItem === index ? (
                        <TextField
                          value={item.requiredQuantity}
                          onChange={(e) => handleItemChange(index, "requiredQuantity", e.target.value)}
                          size="small"
                          type="number"
                          inputProps={{ min: 0 }}
                        />
                      ) : (
                        <Typography variant="body2">{item.requiredQuantity || 1}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingItem === index ? (
                        <TextField
                          value={item.cost}
                          onChange={(e) => handleItemChange(index, "cost", e.target.value)}
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      ) : (
                        <Typography variant="body2">₹{item.cost}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingItem === index ? (
                        <TextField
                          value={item.amount}
                          onChange={(e) => handleItemChange(index, "amount", e.target.value)}
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      ) : (
                        <Typography variant="body2">₹{item.amount || 0}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingItem === index ? (
                        <TextField
                          value={item.batchNumber}
                          onChange={(e) => handleItemChange(index, "batchNumber", e.target.value)}
                          size="small"
                          fullWidth
                        />
                      ) : (
                        <Typography variant="body2">{item.batchNumber || "N/A"}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingItem === index ? (
                        <TextField
                          value={item.expiryDate}
                          onChange={(e) => handleItemChange(index, "expiryDate", e.target.value)}
                          size="small"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                        />
                      ) : (
                        <Typography variant="body2">{item.expiryDate || "N/A"}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingItem === index ? (
                        <TextField
                          value={item.sgst}
                          onChange={(e) => handleItemChange(index, "sgst", e.target.value)}
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      ) : (
                        <Typography variant="body2">₹{item.sgst || 0}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingItem === index ? (
                        <TextField
                          value={item.cgst}
                          onChange={(e) => handleItemChange(index, "cgst", e.target.value)}
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      ) : (
                        <Typography variant="body2">₹{item.cgst || 0}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingItem === index ? (
                        <TextField
                          value={item.inventoryQuantity}
                          onChange={(e) => handleItemChange(index, "inventoryQuantity", e.target.value)}
                          size="small"
                          type="number"
                          inputProps={{ min: 0 }}
                        />
                      ) : (
                        <Typography variant="body2">{item.inventoryQuantity}</Typography>
                      )}
                    </TableCell>
                    {/* <TableCell>
                      <Chip
                        label={item.bomExists ? "Update BOM" : "New BOM Item"}
                        color={item.bomExists ? "warning" : "success"}
                        size="small"
                      />
                    </TableCell> */}
                    <TableCell>
                      {editingItem === index ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleSaveItem(index)}
                            color="primary"
                          >
                            <SaveIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleCancelEdit(index)}
                            color="secondary"
                          >
                            <CancelIcon />
                          </IconButton>
                        </Box>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleEditItem(index)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteItem(index)}
                        color="error"
                        title="Delete this item"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />

          {items.length > 0 && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Summary
              </Typography>
              <Typography variant="body2">
                Total Items: {items.length}
              </Typography>
              <Typography variant="body2">
                New BOM Items: {items.filter(item => !item.bomExists).length}
              </Typography>
              <Typography variant="body2">
                BOM Updates: {items.filter(item => item.bomExists).length}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                Total Inventory Quantity: {items.reduce((sum, item) => sum + (parseInt(item.inventoryQuantity) || 0), 0)}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                Total Amount: ₹{items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toFixed(2)}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                Total SGST: ₹{items.reduce((sum, item) => sum + (parseFloat(item.sgst) || 0), 0).toFixed(2)}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                Total CGST: ₹{items.reduce((sum, item) => sum + (parseFloat(item.cgst) || 0), 0).toFixed(2)}
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="primary">
                Grand Total: ₹{(items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0) + 
                                items.reduce((sum, item) => sum + (parseFloat(item.sgst) || 0), 0) + 
                                items.reduce((sum, item) => sum + (parseFloat(item.cgst) || 0), 0)).toFixed(2)}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
        <Button onClick={handleClose} disabled={loading} variant="outlined" color="primary" sx={FORM_CANCEL_BUTTON_SX}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          sx={FORM_SUBMIT_BUTTON_SX}
          disabled={loading || items.length === 0}
        >
          {loading ? "Adding to Inventory..." : "Add to Inventory"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PathOCRResultDialog;
